import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    VerticalAlign,
    HeightRule,
    TableLayoutType,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, FULL_WIDTH, SCALE, createCommonHeader, createCommonFooter } from "@/utils/wordGenerator/common.js";

export const createCPTemp3 = async (cpData, startPage) => {
    const style = cpData.style || {};
    const sections = [];
    const projectsPerPage = 4;

    const BODY_HEIGHT = 13170;
    const GAP_BETWEEN_ROWS = 400;

    const SIDE_PAD_PERCENT = 4;
    const COL_WIDTH_PERCENT = 43;
    const GAP_WIDTH_PERCENT = 6;
    const IMG_HEIGHT = 3200;
    const CELL_MARGIN = 150;

    const actualColWidthDxa = Math.floor(FULL_WIDTH * (COL_WIDTH_PERCENT / 100)) - (CELL_MARGIN * 2);

    let currentAbsPage = startPage || 1;

    for (let i = 0; i < cpData.projects.length; i += projectsPerPage) {
        const pageProjects = cpData.projects.slice(i, i + projectsPerPage);
        const projectCells = [];

        for (const proj of pageProjects) {
            const children = [];

            // 1. Title (Centered)
            children.push(new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [new TextRun({
                    text: proj.projectName || "N/A",
                    bold: style.heading?.bold || true,
                    italics: style.heading?.italic,
                    underline: style.heading?.underline ? {} : undefined,
                    size: (style.heading?.size || 14) * SCALE,
                    color: fixColor(style.heading?.color || "#FF0000"), // Red in UI?
                    font: style.heading?.font
                })],
                spacing: { after: 120 }
            }));

            // 2. Stats Line (Centered)
            const completionYear = proj.constructionCompletionDate ? new Date(proj.constructionCompletionDate).getFullYear() : 'N/A';
            const statsText = `${proj.city || 'N/A'}, ${proj.stateProvince || 'N/A'}`;
            const detailsText = `Size: ${proj.totalSFT || proj.size || 'N/A'} SF | Value: $${proj.value || 'N/A'}`;
            const completionText = `Completion: ${completionYear}`;
            children.push(new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [new TextRun({
                    text: statsText,
                    size: (style.label?.size || 10) * SCALE,
                    color: fixColor(style.label?.color || "#545454"),
                    font: style.label?.font,
                    bold: style.label?.bold,
                    italics: style.label?.italic,
                    underline: style.label?.underline ? {} : undefined,
                })],
                spacing: { after: 60 }
            }));

            children.push(new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [new TextRun({
                    text: detailsText,
                    size: (style.label?.size || 10) * SCALE,
                    color: fixColor(style.label?.color || "#545454"),
                    font: style.label?.font,
                    bold: style.label?.bold,
                    italics: style.label?.italic,
                    underline: style.label?.underline ? {} : undefined,
                })],
                spacing: { after: 60 }
            }));

            children.push(new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [new TextRun({
                    text: completionText,
                    size: (style.label?.size || 10) * SCALE,
                    color: fixColor(style.label?.color || "#545454"),
                    font: style.label?.font,
                    bold: style.label?.bold,
                    italics: style.label?.italic,
                    underline: style.label?.underline ? {} : undefined,
                })],
                spacing: { after: 150 }
            }));

            // 3. Image (Cover)
            const photo = proj.uploadedPhoto || proj.photoURL;
            if (photo) {
                const imgData = await fetchImageWithDimensions(photo);
                if (imgData) {
                    children.push(new Table({
                        width: { size: 100, type: WidthType.PERCENTAGE },
                        borders: {
                            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                        },
                        rows: [
                            new TableRow({
                                height: { value: IMG_HEIGHT, rule: HeightRule.EXACT },
                                children: [
                                    new TableCell({
                                        children: [new Paragraph({
                                            alignment: AlignmentType.CENTER,
                                            children: [fitImage(imgData, actualColWidthDxa, IMG_HEIGHT, "cover")]
                                        })]
                                    })
                                ]
                            })
                        ]
                    }));
                }
            } else {
                children.push(new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                        insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                    },
                    rows: [
                        new TableRow({
                            height: { value: IMG_HEIGHT, rule: HeightRule.EXACT },
                            children: [
                                new TableCell({
                                    shading: { fill: "FFFFFF" },
                                    children: []
                                })
                            ]
                        })
                    ]
                }));
            }
            children.push(new Paragraph({ spacing: { before: 0, after: 0 } }));

            // 4. Client (Centered)
            children.push(new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                    new TextRun({
                        text: "Client: ",
                        bold: style.label?.bold || true,
                        italics: style.label?.italic,
                        underline: style.label?.underline ? {} : undefined,
                        size: (style.label?.size || 10) * SCALE,
                        color: fixColor(style.label?.color || "#545454"),
                        font: style.label?.font
                    }),
                    new TextRun({
                        text: proj.clientAccount?.accountName || 'N/A',
                        size: (style.text?.size || 10) * SCALE,
                        color: fixColor(style.text?.color || "#000000"),
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline ? {} : undefined,
                    })
                ],
                spacing: { after: 60 }
            }));

            // 5. Designers (Centered)
            const designers = proj.designerAccounts?.map(d => d.accountName).join(', ') || 'N/A';
            children.push(new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                    new TextRun({
                        text: "Designers: ",
                        bold: style.label?.bold || true,
                        italics: style.label?.italic,
                        underline: style.label?.underline ? {} : undefined,
                        size: (style.label?.size || 10) * SCALE,
                        color: fixColor(style.label?.color || "#545454"),
                        font: style.label?.font
                    }),
                    new TextRun({
                        text: designers,
                        size: (style.text?.size || 10) * SCALE,
                        color: fixColor(style.text?.color || "#000000"),
                        font: style.text?.font,
                        bold: style.text?.bold,
                        italics: style.text?.italic,
                        underline: style.text?.underline ? {} : undefined,
                    })
                ]
            }));

            projectCells.push(new TableCell({
                width: { size: COL_WIDTH_PERCENT, type: WidthType.PERCENTAGE },
                shading: { fill: fixColor(style.colorScheme?.body || "#EEEEEE") },
                margins: { top: 200, bottom: 200, left: 200, right: 200 },
                children: children
            }));
        }

        // Filler for grid
        while (projectCells.length < 4) {
            projectCells.push(new TableCell({
                width: { size: COL_WIDTH_PERCENT, type: WidthType.PERCENTAGE },
                children: []
            }));
        }

        const headerTable = createCommonHeader(cpData.title, fixColor(style.colorScheme?.header || "#545454"));
        const footerTable = createCommonFooter(currentAbsPage, fixColor(style.colorScheme?.footer || "#545454"));

        const rowHeight = Math.floor((BODY_HEIGHT - GAP_BETWEEN_ROWS) / 2);
        const bodyTable = new Table({
            width: { size: FULL_WIDTH, type: WidthType.DXA },
            layout: TableLayoutType.FIXED,
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            },
            rows: [
                new TableRow({
                    height: { value: rowHeight, rule: HeightRule.EXACT },
                    children: [
                        new TableCell({ width: { size: SIDE_PAD_PERCENT, type: WidthType.PERCENTAGE }, children: [] }),
                        projectCells[0],
                        new TableCell({ width: { size: GAP_WIDTH_PERCENT, type: WidthType.PERCENTAGE }, children: [] }),
                        projectCells[1],
                        new TableCell({ width: { size: SIDE_PAD_PERCENT, type: WidthType.PERCENTAGE }, children: [] }),
                    ],
                }),
                new TableRow({
                    height: { value: GAP_BETWEEN_ROWS, rule: HeightRule.EXACT },
                    children: [new TableCell({ children: [] })],
                }),
                new TableRow({
                    height: { value: rowHeight, rule: HeightRule.EXACT },
                    children: [
                        new TableCell({ width: { size: SIDE_PAD_PERCENT, type: WidthType.PERCENTAGE }, children: [] }),
                        projectCells[2],
                        new TableCell({ width: { size: GAP_WIDTH_PERCENT, type: WidthType.PERCENTAGE }, children: [] }),
                        projectCells[3],
                        new TableCell({ width: { size: SIDE_PAD_PERCENT, type: WidthType.PERCENTAGE }, children: [] }),
                    ],
                }),
            ],
        });

        sections.push({
            properties: {
                page: {
                    margin: { top: 0, right: 0, bottom: 0, left: 0 },
                },
            },
            children: [
                headerTable,
                new Paragraph({ spacing: { before: 600, after: 0 }, children: [] }),
                bodyTable,
                new Paragraph({ spacing: { before: 600, after: 0 }, children: [] }),
                footerTable
            ],
        });
        currentAbsPage++;
    }
    return sections;
};
